/**
 * Viewport: the mapping between document space and canvas pixels.
 *
 * Document space is inches with **y increasing upward**, as CIRCAD uses.
 * Canvas space is pixels with y increasing downward. Every conversion goes
 * through here so the flip lives in exactly one place.
 */

export interface Point {
  x: number;
  y: number;
}

export class Viewport {
  /** Document coordinate at the centre of the canvas. */
  cx = 0;
  cy = 0;
  /** Pixels per inch. */
  scale = 300;

  /** Canvas size in CSS pixels. */
  width = 0;
  height = 0;
  /** Device pixel ratio the backing store was sized for. */
  dpr = 1;

  /**
   * Setup ▸ Mirror View: flip the sheet left-to-right so the board is seen from
   * its other side. It is a *view* transform — the geometry is untouched, so
   * coordinates, snapping and editing all keep working through it.
   */
  mirrored = false;

  /** One-deep view history for Zoom Previous, as the original keeps. */
  private prev: { cx: number; cy: number; scale: number } | null = null;

  /** Remember the current view so {@link restorePrevious} can return to it. */
  rememberView(): void {
    this.prev = { cx: this.cx, cy: this.cy, scale: this.scale };
  }

  /** Zoom Previous: swap the view with the last remembered one. */
  restorePrevious(): boolean {
    if (!this.prev) return false;
    const now = { cx: this.cx, cy: this.cy, scale: this.scale };
    this.cx = this.prev.cx;
    this.cy = this.prev.cy;
    this.scale = this.prev.scale;
    this.prev = now;
    return true;
  }

  toScreenX(x: number): number {
    const d = (x - this.cx) * this.scale;
    return this.width / 2 + (this.mirrored ? -d : d);
  }

  toScreenY(y: number): number {
    return this.height / 2 - (y - this.cy) * this.scale;
  }

  toWorldX(sx: number): number {
    const d = (sx - this.width / 2) / this.scale;
    return this.cx + (this.mirrored ? -d : d);
  }

  toWorldY(sy: number): number {
    return this.cy - (sy - this.height / 2) / this.scale;
  }

  toWorld(sx: number, sy: number): Point {
    return { x: this.toWorldX(sx), y: this.toWorldY(sy) };
  }

  /** Document-space length of one screen pixel — the visual precision limit. */
  get pixelSize(): number {
    return 1 / this.scale;
  }

  /** Zoom about a fixed screen point, so the point under the cursor stays put. */
  zoomAt(sx: number, sy: number, factor: number, min = 4, max = 40000): void {
    const wx = this.toWorldX(sx);
    const wy = this.toWorldY(sy);
    this.scale = Math.max(min, Math.min(max, this.scale * factor));
    // Solve toWorldX(sx) === wx for the new centre; a mirrored view runs the
    // x offset the other way, so the sign follows the flip.
    const dx = (sx - this.width / 2) / this.scale;
    this.cx = this.mirrored ? wx + dx : wx - dx;
    this.cy = wy + (sy - this.height / 2) / this.scale;
  }

  panByPixels(dx: number, dy: number): void {
    // Dragging right moves the sheet right; mirrored, the world runs the other
    // way, so the drag has to follow it or panning feels reversed.
    this.cx += (this.mirrored ? dx : -dx) / this.scale;
    this.cy += dy / this.scale;
  }

  /** Frame a document-space rectangle with a little margin. */
  fit(x1: number, y1: number, x2: number, y2: number, margin = 0.06): void {
    const w = Math.abs(x2 - x1);
    const h = Math.abs(y2 - y1);
    this.cx = (x1 + x2) / 2;
    this.cy = (y1 + y2) / 2;
    if (w <= 0 || h <= 0 || this.width <= 0 || this.height <= 0) {
      this.scale = 300;
      return;
    }
    const pad = 1 + margin * 2;
    this.scale = Math.min(this.width / (w * pad), this.height / (h * pad));
    this.scale = Math.max(4, Math.min(40000, this.scale));
  }

  /** Visible document-space rectangle, for culling. Normalised, so it stays a
   *  proper rectangle when the view is mirrored and screen x runs backwards. */
  bounds(): { x1: number; y1: number; x2: number; y2: number } {
    const ax = this.toWorldX(0);
    const bx = this.toWorldX(this.width);
    return {
      x1: Math.min(ax, bx),
      y1: this.toWorldY(this.height),
      x2: Math.max(ax, bx),
      y2: this.toWorldY(0),
    };
  }
}

/** Snap a document-space coordinate to the grid. */
export function snap(v: number, grid: number): number {
  if (grid <= 0) return v;
  return Math.round(v / grid) * grid;
}

/**
 * Ortho snap, in the original's own four states (`wp->fOrtho`, OGxyz00.cpp:378).
 *
 * Not a switch. It was one here, meaning "hold to H, V or 45°", which is only
 * the last of the four — and it made a signal wire able to run diagonally, which
 * in the original it cannot.
 *
 *   0  off — the point goes where you point
 *   1  vertical and horizontal only
 *   2  the two diagonals only
 *   3  vertical, horizontal and both diagonals
 *
 * `S T` (Setup ▸ Ortho Mode) and the `T` key both step `(mode + 1) & 3`, so all
 * four are reachable in the order the original puts them.
 */
export type OrthoMode = 0 | 1 | 2 | 3;

/** The mode a command works in, and whether that is any constraint at all. */
export const ORTHO_NAMES: Record<OrthoMode, string> = {
  0: 'off',
  1: 'H/V',
  2: '45°',
  3: 'H/V/45°',
};

/**
 * Hold a segment to the directions `mode` allows.
 *
 * The original's mode 2 has a second form used while a single line is being
 * placed, which shortens the segment along one axis so that a diagonal run and
 * the horizontal after it both land on the grid. That is not reproduced: it
 * depends on the ghost-line state its cursor list keeps, and the plain 45°
 * constraint is what mode 2 does everywhere else.
 */
export function orthoConstrain(
  x1: number, y1: number, x2: number, y2: number, mode: OrthoMode = 3,
): Point {
  if (mode === 0) return { x: x2, y: y2 };

  const dx = x2 - x1;
  const dy = y2 - y1;
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);

  // Straight onto the nearer axis. No 45° to lose out to, so the whole circle
  // divides between horizontal and vertical at the diagonal.
  if (mode === 1) {
    return adx >= ady ? { x: x2, y: y1 } : { x: x1, y: y2 };
  }

  // Diagonals only: the shorter leg sets the length, so the run is exactly 45°.
  const diagonal = (): Point => {
    const d = Math.min(adx, ady);
    return { x: x1 + Math.sign(dx) * d, y: y1 + Math.sign(dy) * d };
  };
  if (mode === 2) return diagonal();

  // All four, with the axes taking the wider share — 2.414 is tan(67.5°), the
  // bisector between an axis and a diagonal.
  if (adx > ady * 2.414) return { x: x2, y: y1 };            // horizontal
  if (ady > adx * 2.414) return { x: x1, y: y2 };            // vertical
  return diagonal();
}
